import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useToneGame } from './ToneGameContext';
import { useAuth } from '../../context/FirebaseAuthContext';
import { useGameSession } from '../../context/GameSessionContext';
import { GameResultSaver } from '../../utils/gameResultSaver';
import { TonePad as TonePadType } from './ToneGameContext';
import { useGameEngine } from '../../hooks/useGameEngine';
import { ToneHUD } from './ToneHUD';

const TONE_PADS: TonePadType[] = [
  { id: 'C4', color: '#FF0000', tone: '/sounds/tones/c4.mp3', note: 'C4' },  // Red - Middle C
  { id: 'E4', color: '#00FF00', tone: '/sounds/tones/e4.mp3', note: 'E4' },  // Green - E
  { id: 'G4', color: '#0000FF', tone: '/sounds/tones/g4.mp3', note: 'G4' },  // Blue - G
  { id: 'B4', color: '#FFFF00', tone: '/sounds/tones/b4.mp3', note: 'B4' },  // Yellow - B
];

const ToneGame: React.FC = () => {
  const { state, dispatch } = useToneGame();
  const { user } = useAuth();
  const { currentSession } = useGameSession();
  const gameEngine = useGameEngine();
  const audioCache = useRef<{ [key: string]: HTMLAudioElement }>({});
  const [activePad, setActivePad] = useState<string | null>(null);
  const [lastSavedRound, setLastSavedRound] = useState(0);

  // Load and cache all sounds at the start
  useEffect(() => {
    TONE_PADS.forEach(pad => {
      const audio = new Audio(pad.tone);
      audio.load();
      audioCache.current[pad.tone] = audio;
    });

    // Cleanup on unmount
    return () => {
      Object.values(audioCache.current).forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    };
  }, []);

  // Save results to database when round is completed
  useEffect(() => {
    // Trigger save when completedSequences increases and we haven't saved this round yet
    if (state.stats.completedSequences > 0 && state.stats.completedSequences > lastSavedRound) {
      console.log('ToneGame: Triggering database save for completed sequences:', state.stats.completedSequences);
      console.log('ToneGame: Current game state:', {
        currentRound: state.currentRound,
        gamePhase: state.gamePhase,
        completedSequences: state.stats.completedSequences,
        accuracy: state.stats.accuracy,
        sequenceLength: state.sequence.length,
        reactionTimes: state.stats.reactionTimes
      });
      
      const saveRoundToDatabase = async () => {
        const accuracy = state.stats.accuracy;
        
        const averageReactionTime = state.stats.reactionTimes.length > 0
          ? state.stats.reactionTimes.reduce((a, b) => a + b, 0) / state.stats.reactionTimes.length
          : 0;

        // Calculate a basic score based on accuracy and reaction time
        const baseScore = accuracy * 10;
        const timeBonus = averageReactionTime > 0 ? Math.max(0, 1000 - averageReactionTime) / 10 : 0;
        const totalScore = Math.round(baseScore + timeBonus);

        const resultData = {
          sessionId: currentSession?.sessionId || `session-${Date.now()}`,
          timestamp: new Date().toISOString(),
          round: state.stats.completedSequences, // Use completed sequences as round number
          sequenceLength: state.sequence.length || 1, // Ensure we have a valid sequence length
          accuracyPercentage: accuracy,
          score: totalScore,
          reactionTimeMs: Math.round(averageReactionTime),
          difficultyLevel: Math.round(state.difficulty), // Convert to integer
          isCompleted: true,
          originalSequence: JSON.stringify(state.sequence),
          playerSequence: JSON.stringify(state.playerSequence),
          timingData: JSON.stringify({
            reactionTimes: state.stats.reactionTimes,
            completedSequences: state.stats.completedSequences,
            errorsPerRound: state.stats.errorsPerRound
          })
        };

        console.log('ToneGame: Saving result data:', resultData);
        await GameResultSaver.saveResultSafely(
          () => GameResultSaver.saveToneMemoryResult(resultData),
          'ToneMemory',
          resultData
        );

        // Mark this round as saved
        setLastSavedRound(state.stats.completedSequences);
      };

      saveRoundToDatabase();
    }
  }, [state.stats.completedSequences, state.currentRound, state.gamePhase, state.sequence, state.playerSequence, 
      state.stats, state.difficulty, currentSession, lastSavedRound]);

  const playTone = useCallback(async (pad: TonePadType): Promise<void> => {
    try {
      console.log('Playing tone:', pad.id, pad.tone); // Debug log
      
      let audio = audioCache.current[pad.tone];
      if (!audio) {
        console.log('Creating new audio for:', pad.tone); // Debug log
        audio = new Audio(pad.tone);
        audioCache.current[pad.tone] = audio;
      }

      setActivePad(pad.id);
      audio.currentTime = 0;
      
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        await playPromise;
        console.log('Tone started playing:', pad.id); // Debug log
        
        return new Promise<void>((resolve) => {
          audio.onended = () => {
            console.log('Tone finished playing:', pad.id); // Debug log
            setActivePad(null);
            resolve();
          };
        });
      }
    } catch (error) {
      console.error('Failed to play sound:', error);
      setActivePad(null);
    }
  }, []);

  const playSequence = useCallback(async (sequence: TonePadType[]) => {
    try {
      console.log('Playing sequence:', sequence);

      if (!sequence || sequence.length === 0) {
        console.error('No sequence to play');
        return;
      }

      // Play each pad in the sequence
      for (const pad of sequence) {
        console.log('Playing pad:', pad.id);
        await playTone(pad);
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second between tones
      }
    } catch (error) {
      console.error('Error playing sequence:', error);
    }
  }, [playTone]);

  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState(false);

  const handlePadClick = useCallback(async (pad: TonePadType) => {
    if (state.gamePhase !== 'repeating') return;

    const startTime = performance.now();
    await playTone(pad);
    dispatch({ type: 'PLAYER_INPUT', pad });
    dispatch({ type: 'RECORD_REACTION_TIME', time: performance.now() - startTime });
    
    // Check if the input is correct
    const currentIndex = state.playerSequence.length;
    const isCorrect = pad.id === state.sequence[currentIndex].id;

    if (!isCorrect) {
      setFeedbackMessage('❌ Incorrect! Try again');
      setShowFeedback(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowFeedback(false);
      dispatch({ type: 'RESET_SEQUENCE' });
      return;
    }

    // If this was the last input in the sequence
    if (currentIndex === state.sequence.length - 1) {
      setFeedbackMessage('🎉 Nice work! Round Complete!');
      setShowFeedback(true);
      
      // Calculate performance metrics
      const reactionTime = state.stats.reactionTimes[state.stats.reactionTimes.length - 1];
      const normalizedReactionTime = Math.min(reactionTime, 2000) / 2000;
      const accuracy = ((currentIndex + 1) / state.sequence.length) * 100;
      
      // Calculate difficulty change based on performance
      const performanceScore = ((accuracy / 100) + (1 - normalizedReactionTime)) / 2;
      const difficultyChange = (performanceScore - 0.5) * 0.2;

      // Apply difficulty adjustment
      dispatch({ type: 'ADJUST_DIFFICULTY', change: difficultyChange });
      
      // Show success message for a moment
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Complete the round and increment counter
      console.log('ToneGame: Dispatching COMPLETE_ROUND for round', state.currentRound);
      dispatch({ type: 'COMPLETE_ROUND' });
      
      // Show round transition message
      setFeedbackMessage(`🎯 Get Ready for Round ${state.currentRound + 1}!`);
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowFeedback(false);
    }
  }, [state.gamePhase, state.playerSequence.length, state.sequence.length, state.stats, dispatch, playTone]);

  const startNewRound = useCallback(async () => {
    try {
      console.log('Starting new round...');
      
      // Reset the state first
      dispatch({ type: 'START_SEQUENCE' });
      
      // Generate sequence based on the current round
      const sequence = [];
      // Start with 2 tones and add one each round
      const length = Math.min(2 + state.currentRound - 1, 8); // Cap at 8 tones maximum
      console.log('Sequence length for round:', state.currentRound, 'will be:', length);
      
      // Generate sequence
      for (let i = 0; i < length; i++) {
        const pad = TONE_PADS[Math.floor(Math.random() * TONE_PADS.length)];
        sequence.push(pad);
        // Add to sequence state
        dispatch({ type: 'ADD_TO_SEQUENCE', pad });
      }
      
      console.log('Generated sequence:', sequence);
      
      // Small delay before playing
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Play each pad in sequence with delay
      for (const pad of sequence) {
        await playTone(pad);
        // Wait between tones
        await new Promise(resolve => setTimeout(resolve, 800));
      }
      
      // Switch to repeating phase after playing sequence
      await new Promise(resolve => setTimeout(resolve, 500));
      dispatch({ type: 'SET_GAME_PHASE', phase: 'repeating' });
    } catch (error) {
      console.error('Error starting new round:', error);
    }
  }, [state.currentRound, dispatch, playTone]);

  const exportResults = useCallback(() => {
    const results = {
      accuracy: state.stats.accuracy,
      averageReactionTime: state.stats.reactionTimes.reduce((a: number, b: number) => a + b, 0) / state.stats.reactionTimes.length || 0,
      completedSequences: state.stats.completedSequences,
      errorsPerRound: state.stats.errorsPerRound,
    };

    const csvContent = [
      'Round,Accuracy,Reaction Time,Errors',
      `${state.currentRound},${results.accuracy},${results.averageReactionTime},${state.stats.errorsPerRound.length}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'tone-game-results.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [state.currentRound, state.stats]);

  // Helper function to get button text
  const getButtonText = () => {
    if (state.stats.completedSequences === 0) {
      return '🎮 Start Round 1';
    }
    if (state.gamePhase === 'ready') {
      return `▶️ Play Round ${state.currentRound}`;
    }
    return '⏳ Wait...';
  };

  return (
    <div className="min-h-screen bg-gray-900 py-2 sm:py-4">
      <div className="max-w-7xl mx-auto px-3 sm:px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Right side - HUD */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <ToneHUD />
          </div>

          {/* Left side - Game */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <header className="text-center mb-4 sm:mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Tone Memory</h1>
              <div className="text-sm sm:text-base font-medium text-white score bg-gray-800 py-2 px-4 rounded-full inline-block">
                {state.gamePhase === 'listening'
                  ? `👂 Round ${state.currentRound}: Listen carefully...`
                  : state.gamePhase === 'repeating'
                  ? '🎮 Now repeat the sequence!'
                  : `🎯 Ready for Round ${state.currentRound}?`}
              </div>
              {showFeedback && (
                <div className={`mt-4 text-lg font-medium ${
                  feedbackMessage.includes('❌') ? 'text-red-400' : 'text-green-400'
                } animate-bounce`}>
                  {feedbackMessage}
                </div>
              )}
            </header>

        <div className="relative mx-auto mb-8 sm:mb-12" 
             style={{
               width: 'min(80vw, 320px)',
               height: 'min(80vw, 320px)',
             }}>
          {/* Circular background */}
          <div className="absolute inset-0 rounded-full bg-gray-800 shadow-2xl" />
          
          {/* Round counter in center */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">{state.currentRound}</div>
              <div className="text-gray-400 text-xs sm:text-sm mt-1">ROUND</div>
            </div>
          </div>

          {/* Tone pads positioned in a circle */}
          {TONE_PADS.map((pad, index) => {
            const angle = (index * (360 / TONE_PADS.length) - 45) * (Math.PI / 180);
            // Responsive radius using viewport-based calculation
            const containerSize = Math.min(window.innerWidth * 0.8, 320);
            const radius = containerSize * 0.28; // 28% of container size for optimal spacing
            
            return (
              <button
                key={pad.id}
                data-testid={`tone-pad-${pad.id}`}
                className={`absolute w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full transition-all ${
                  activePad === pad.id
                    ? 'scale-110 ring-4 ring-white shadow-lg brightness-150'
                    : 'hover:scale-105'
                } focus:outline-none`}
                style={{
                  backgroundColor: pad.color,
                  // Use CSS calc() for responsive positioning
                  left: `calc(50% + ${radius * Math.cos(angle)}px)`,
                  top: `calc(50% + ${radius * Math.sin(angle)}px)`,
                  transform: 'translate(-50%, -50%)',
                  transition: 'all 0.15s ease-in-out'
                }}
                onClick={() => handlePadClick(pad)}
                disabled={state.gamePhase !== 'repeating'}
              />
            );
          })}
        </div>

        <div className="flex justify-center space-x-4 mb-8">
          <button
            className={`px-8 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 text-lg font-medium transition-all ${
              state.gamePhase === 'ready' 
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-gray-600 text-gray-300 cursor-not-allowed'
            }`}
            onClick={startNewRound}
            disabled={state.gamePhase !== 'ready'}
          >
            {getButtonText()}
          </button>
          
          {/* Replay sequence button */}
          <button
            className={`px-6 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-gray-900 text-lg font-medium transition-all ${
              state.gamePhase === 'repeating' && state.sequence.length > 0
                ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                : 'bg-gray-600 text-gray-300 cursor-not-allowed'
            }`}
            onClick={() => playSequence(state.sequence)}
            disabled={state.gamePhase !== 'repeating' || state.sequence.length === 0}
          >
            🔄 Replay Sequence
          </button>

          {/* Export results button */}
          <button
            className="px-6 py-3 rounded-full bg-green-600 hover:bg-green-700 text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 text-lg font-medium transition-all"
            onClick={exportResults}
          >
            📊 Export Results
          </button>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToneGame;